import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { Gift, Sparkles } from 'lucide-react';
import { useDailyBonus } from '../hooks/useDailyBonus';
import { useAuth } from '../contexts/AuthContext';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { fireConfetti } from '../utils/confetti';
import { haptic } from '../utils/haptic';
import { playSound } from '../utils/sound';
import { queuePendingCredit, flushPendingCredits } from '../utils/pendingCredits';
import { Sixy } from './Sixy';

/**
 * Rotas onde outro overlay/modal/scanner já ocupa a tela e o Daily Bonus
 * NÃO deve aparecer (BUG-03/04). Quando o user sair dessas rotas, o trigger
 * re-avalia (deps inclui location.pathname) e dispara normalmente.
 */
const CONFLICTING_ROUTE_PREFIXES = ['/parceiros/', '/scanner', '/admin'];

function isConflictingRoute(pathname: string): boolean {
  return CONFLICTING_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * Modal "Bônus diário" — primeira abertura do dia.
 *
 * Tem duas fases:
 *  1. **Pré-resgate**: mostra um "presente fechado" pulsando. CTA "Abrir".
 *  2. **Pós-resgate**: revela o valor sorteado com explosão de confete +
 *     som de cha-ching. Botão "Massa!" pra fechar.
 *
 * Aparece automaticamente quando `useDailyBonus().available === true` E
 * o usuário tem profile carregado (não polui anônimo / não-logado).
 *
 * Trade-off do delay: aguardamos ~1.5s após mount pra não brigar com
 * splash screen e outros overlays (UpdatePrompt, AvatarOnboarding).
 */
interface DailyBonusModalProps {
  /** Chamado quando o user toca "Faz check-in pra liberar" no estado locked. */
  onNavigateToCheckin?: () => void;
}

export function DailyBonusModal({ onNavigateToCheckin }: DailyBonusModalProps = {}) {
  const { available, amount, requiresCheckin, claim } = useDailyBonus();
  const { profile, updateUserProfile } = useAuth();
  const reducedMotion = useReducedMotion();
  const location = useLocation();
  const [phase, setPhase] = useState<
    'hidden' | 'preReveal' | 'revealing' | 'revealed' | 'locked'
  >('hidden');
  // Snapshot do valor creditado, salvo no momento do claim. Necessário porque
  // o hook useDailyBonus zera `amount` e `available` imediatamente após o
  // claim — sem esse snapshot local, a fase 'revealed' renderizaria com
  // `amount=null` ou nem chegaria a renderizar (componente desmonta por
  // `!available`).
  const [creditedAmount, setCreditedAmount] = useState<number | null>(null);

  // Ref pro setTimeout do reveal — usada pra cleanup no unmount. Sem isso,
  // se o componente desmontar entre setPhase('revealing') e o callback (ex.:
  // rota muda, profile re-fetch desmonta tudo), o claim() e o setState ainda
  // disparam em componente morto → warning React + crédito de pontos fantasma.
  const revealTimerRef = useRef<number | null>(null);

  // Cleanup global do timer ao desmontar.
  useEffect(() => {
    return () => {
      if (revealTimerRef.current !== null) {
        window.clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, []);

  // Flush da fila de créditos pendentes (créditos de dailyBonus que falharam
  // antes por rede ruim). Roda:
  //   - 1x quando profile carrega (mount com profile.id válido)
  //   - Quando user volta online após queda
  // Em paralelo: applyDelta calcula `points` baseado no snapshot atual do
  // profile, pra não pisar em update concorrente.
  useEffect(() => {
    if (!profile?.uid) return;
    let cancelled = false;

    const tryFlush = async () => {
      const result = await flushPendingCredits(async (delta) => {
        if (cancelled) throw new Error('cancelled');
        // Re-lê profile.points (snapshot atual via closure) — não acumula
        // crédito perdido se profile foi atualizado externamente.
        await updateUserProfile({ points: (profile.points || 0) + delta });
      });
      if (result.processed > 0 && !cancelled) {
        console.info(
          `[pendingCredits] flush OK — ${result.processed} créditos (+${result.totalAmount} pts)`,
        );
      }
    };

    void tryFlush();
    window.addEventListener('online', tryFlush);
    return () => {
      cancelled = true;
      window.removeEventListener('online', tryFlush);
    };
    // profile.uid muda quando o user troca de conta — re-flush nesse caso.
    // profile.points NÃO entra nas deps senão refaz a cada update bem-sucedido
    // (loop). Lemos via closure no momento de cada flush.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.uid]);

  // Trigger: profile carregado, é student, bônus disponível, ainda não exibiu.
  // Decide fase inicial:
  //  - requiresCheckin: 'locked' (preview + CTA pra fazer check-in)
  //  - senão: 'preReveal' (presente normal pra abrir)
  //
  // Guard contra BUG-03/04: se o user está em rota com outro modal/scanner
  // aberto (/parceiros/*, /scanner, /admin/*), pula o trigger. Quando ele
  // navegar de volta pra rota safe, o effect re-roda (location.pathname
  // está nas deps) e o bônus aparece normalmente.
  useEffect(() => {
    if (!available || !profile || profile.role !== 'student') return;
    if (isConflictingRoute(location.pathname)) return;
    // Aguarda 1.5s pra não conflitar com Avatar onboarding / UpdatePrompt /
    // splash. O user já passou disso quando o bonus aparece.
    const initialPhase = requiresCheckin ? 'locked' : 'preReveal';
    const t = window.setTimeout(() => {
      // Re-check no fire time: se o user navegou pra rota conflitante DURANTE
      // o delay de 1.5s (ex: abriu PartnerCard rapidinho), aborta o setPhase.
      if (isConflictingRoute(window.location.pathname)) return;
      setPhase(initialPhase);
    }, 1500);
    return () => window.clearTimeout(t);
  }, [available, profile, requiresCheckin, location.pathname]);

  // Safety net: se o bônus JÁ está visível (preReveal/locked) e o user navega
  // pra rota conflitante (raro, mas possível via deep link / back button),
  // esconde temporariamente. Próxima visita à rota safe re-dispara o trigger
  // acima. Não afeta a fase 'revealed' (já é um close manual do user).
  useEffect(() => {
    if ((phase === 'preReveal' || phase === 'locked') &&
        isConflictingRoute(location.pathname)) {
      setPhase('hidden');
    }
  }, [location.pathname, phase]);

  // Quando user faz check-in (evento sixpass:checkin-done dispara recompute
  // no useDailyBonus, requiresCheckin vira false), transiciona locked → preReveal
  // automaticamente. Sem isso o user precisaria fechar e abrir o modal de novo.
  useEffect(() => {
    if (phase === 'locked' && !requiresCheckin && available) {
      setPhase('preReveal');
    }
  }, [phase, requiresCheckin, available]);

  // ESC fecha (somente quando já revelou — antes disso, fechar deixa o
  // bonus pendente pra próxima abertura).
  useEffect(() => {
    if (phase !== 'revealed') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase]);

  const handleOpen = () => {
    if (phase !== 'preReveal') return;
    haptic('medium');
    void playSound('unlock');
    setPhase('revealing');

    // Pausa de ~600ms pra build-up (tremor do presente). Curta o suficiente
    // pra não parecer travado. Guardada em ref pra ser cancelada se desmontar.
    revealTimerRef.current = window.setTimeout(() => {
      revealTimerRef.current = null;
      const credited = claim();
      if (credited === null) {
        // Race ou já resgatou — só fecha.
        setPhase('hidden');
        return;
      }

      // Salva o valor localmente — claim() zerou o estado do hook, então
      // partir daqui a UI lê desse snapshot.
      setCreditedAmount(credited);

      // Som + haptic escalonam por raridade. Comum (10): pop simples sem
      // fanfarra (evita habituação — em 7 dias era "uau", em 30 dias "ah ok").
      // Épico (20-30): chaching + medium haptic. Lendário (50): chaching +
      // success haptic + reverb (built-in do sound engine).
      if (credited >= 50) {
        void playSound('chaching');
        haptic('success');
      } else if (credited >= 20) {
        void playSound('chaching');
        haptic('medium');
      } else {
        // Comum (10): som menor, só "pop" — mantém o reconhecimento de
        // recompensa sem o peso visual/sonoro de algo raro.
        void playSound('pop');
        haptic('light');
      }

      // Confete só pra épico+ (≥20). Comum (10) ganha apenas a animação
      // de "+10" entrando — menos espetáculo, mantém o hábito sem cansar.
      if (!reducedMotion && credited >= 20) {
        void fireConfetti({
          particleCount: credited >= 50 ? 140 : 80,
          spread: 90,
          startVelocity: 36,
          scalar: credited >= 50 ? 1.3 : 1,
          origin: { y: 0.35 },
          colors:
            credited >= 50
              ? ['#fbbf24', '#facc15', '#f59e0b', '#f97316']
              : ['#a855f7', '#f472b6', '#fbbf24'],
        });
      }
      setPhase('revealed');

      // SEGUNDO: credita pontos em background — não bloqueia UI feedback.
      // Se falhar (rede ruim, Supabase down), enfileira pra retry. UX não
      // sofre: o usuário viu "+N Six Coins" instantaneamente, e da próxima
      // vez que o app montar (ou voltar online), a fila processa.
      void (async () => {
        try {
          await updateUserProfile({ points: (profile.points || 0) + credited });
        } catch (err) {
          console.warn('[DailyBonus] falha creditando — enfileirando retry', err);
          queuePendingCredit(credited, 'daily-bonus');
        }
      })();
    }, 600);
  };

  const handleClose = () => {
    setPhase('hidden');
    setCreditedAmount(null);
  };

  // Guard de visibilidade: NÃO podemos usar `!available` aqui porque o claim
  // zera available imediatamente. Em vez disso, contamos com o `phase` —
  // só fica null quando phase === 'hidden'. O trigger acima só sai de hidden
  // se available foi true em algum momento.
  // Focus trap (WCAG 2.1.1) — ativo enquanto modal está visível.
  // Foca o botão "Abrir presente" / "Massa!" no mount, prende Tab cycle,
  // restaura foco no trigger ao fechar.
  const trapRef = useFocusTrap<HTMLDivElement>(phase !== 'hidden');

  if (phase === 'hidden') return null;

  // Valor exibido: durante revealed usa o snapshot local (post-claim);
  // antes do claim usa o `amount` do hook. Fallback 10 (piso atual).
  const finalAmount = creditedAmount ?? amount ?? 10;
  const isLegendary = finalAmount >= 50;
  const isEpic = finalAmount >= 20;

  return (
    <AnimatePresence>
      <motion.div
        ref={trapRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-md px-6"
        onClick={phase === 'revealed' ? handleClose : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby="daily-bonus-title"
      >
        <motion.div
          initial={{ scale: 0.7, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.7, opacity: 0, y: 20 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          className={`relative max-w-sm w-full rounded-3xl border p-6 text-center overflow-hidden ${
            isLegendary
              ? 'border-amber-400/40 bg-gradient-to-br from-amber-500/15 via-slate-900 to-orange-600/15 shadow-[0_0_60px_rgba(251,191,36,0.4)]'
              : isEpic
                ? 'border-pink-400/35 bg-gradient-to-br from-pink-500/12 via-slate-900 to-blue-500/12 shadow-[0_0_44px_rgba(244,114,182,0.3)]'
                : 'border-white/10 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 shadow-2xl'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Sparkles decorativos no fundo (só legendary/epic) */}
          {(isEpic || isLegendary) && (
            <div
              className="absolute inset-0 pointer-events-none"
              aria-hidden="true"
            >
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute"
                  style={{
                    top: `${(i * 13) % 100}%`,
                    left: `${(i * 31) % 100}%`,
                  }}
                  animate={{
                    opacity: [0, 0.8, 0],
                    scale: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 2.5,
                    delay: i * 0.3,
                    repeat: Infinity,
                  }}
                >
                  <Sparkles
                    size={12}
                    className={isLegendary ? 'text-amber-300' : 'text-pink-300'}
                  />
                </motion.div>
              ))}
            </div>
          )}

          <div className="relative">
            <div className="flex justify-center mb-3">
              <Sixy form="cloud" size="md" mood="happy" />
            </div>

            <h2
              id="daily-bonus-title"
              className="text-center text-2xl font-black text-white mb-1 leading-tight"
            >
              {phase === 'revealed' ? (
                <span
                  className="block"
                  role="status"
                  aria-live="polite"
                >
                  <span className="block text-base font-bold text-slate-300 mb-1">
                    {isLegendary ? 'Bingo!' : isEpic ? 'Boa!' : 'Anota aí'}
                  </span>
                  <span
                    className={`block text-3xl font-black bg-gradient-to-r bg-clip-text text-transparent ${
                      isLegendary
                        ? 'from-amber-300 to-orange-400'
                        : isEpic
                          ? 'from-pink-400 to-purple-400'
                          : 'from-blue-400 to-pink-400'
                    }`}
                  >
                    +{finalAmount} Six Coins
                  </span>
                </span>
              ) : phase === 'locked' ? (
                <span className="block">
                  <span className="block text-base font-bold text-slate-300 mb-1">
                    Hoje veio
                  </span>
                  <span
                    className={`block text-3xl font-black bg-gradient-to-r bg-clip-text text-transparent ${
                      isLegendary
                        ? 'from-amber-300 to-orange-400'
                        : 'from-pink-400 to-purple-400'
                    }`}
                  >
                    +{finalAmount} Six Coins
                  </span>
                </span>
              ) : (
                'Tem presente te esperando'
              )}
            </h2>
            <p className="text-xs text-slate-400 mb-5">
              {phase === 'revealed'
                ? isLegendary
                  ? 'Sortudo. Volta amanhã pra mais.'
                  : 'Volta amanhã — todo dia tem um novo.'
                : phase === 'locked'
                  ? 'Bônus maior liberado só depois do teu check-in de hoje. Bora?'
                  : 'Toca pra abrir, sem mistério.'}
            </p>

            {/* Caixa de presente / valor revelado */}
            <div className="flex justify-center mb-5">
              {phase === 'preReveal' && (
                <motion.button
                  type="button"
                  onClick={handleOpen}
                  whileTap={{ scale: 0.9 }}
                  whileHover={{ scale: 1.05 }}
                  animate={{
                    rotate: [-2, 2, -2],
                    y: [0, -3, 0],
                  }}
                  transition={{
                    duration: 1.4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className="flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-blue-500 shadow-2xl shadow-pink-500/40"
                  aria-label="Abrir presente"
                >
                  <Gift size={44} className="text-white" />
                </motion.button>
              )}

              {phase === 'revealing' && (
                <motion.div
                  animate={{
                    scale: [1, 1.2, 0.95, 1.05, 1],
                    rotate: [0, -10, 10, -5, 0],
                  }}
                  transition={{ duration: 0.6 }}
                  className="flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-blue-500"
                >
                  <Gift size={44} className="text-white" />
                </motion.div>
              )}

              {phase === 'revealed' && (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 12 }}
                  className={`flex flex-col items-center justify-center rounded-2xl px-6 py-4 ${
                    isLegendary
                      ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                      : isEpic
                        ? 'bg-gradient-to-br from-pink-500 to-purple-500'
                        : 'bg-gradient-to-br from-blue-500 to-pink-500'
                  } shadow-2xl`}
                >
                  <span className="text-4xl font-black text-white leading-none">
                    +{finalAmount}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/90 mt-1">
                    Six Coins
                  </span>
                  {isLegendary && (
                    <span className="mt-1.5 text-[9px] font-black uppercase tracking-widest text-amber-100">
                      ★ Lendário ★
                    </span>
                  )}
                  {isEpic && !isLegendary && (
                    <span className="mt-1.5 text-[9px] font-black uppercase tracking-widest text-pink-100">
                      ✦ Épico ✦
                    </span>
                  )}
                </motion.div>
              )}

              {phase === 'locked' && (
                <motion.div
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className={`relative flex flex-col items-center justify-center rounded-2xl px-6 py-4 ${
                    isLegendary
                      ? 'bg-gradient-to-br from-amber-400/80 to-orange-500/80'
                      : 'bg-gradient-to-br from-pink-500/80 to-purple-500/80'
                  } shadow-2xl`}
                >
                  {/* Cadeado pra deixar claro que tá bloqueado */}
                  <div
                    className="absolute -top-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 border-2 border-white/20"
                    aria-hidden="true"
                  >
                    <span className="text-xs">🔒</span>
                  </div>
                  <span className="text-4xl font-black text-white leading-none opacity-90">
                    +{finalAmount}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/90 mt-1">
                    Six Coins
                  </span>
                </motion.div>
              )}
            </div>

            {phase === 'revealed' && (
              <button
                type="button"
                onClick={handleClose}
                className="press-feedback w-full rounded-full bg-white text-slate-900 py-2.5 text-sm font-black uppercase tracking-wide hover:brightness-90 transition"
              >
                Massa!
              </button>
            )}

            {phase === 'locked' && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    haptic('medium');
                    onNavigateToCheckin?.();
                    handleClose();
                  }}
                  className="w-full rounded-full bg-gradient-to-r from-pink-500 to-blue-500 text-white py-3 text-sm font-black uppercase tracking-wide shadow-lg shadow-pink-500/30 hover:brightness-110 transition"
                >
                  Bora fazer check-in
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="press-feedback w-full py-2 text-xs font-bold text-slate-400 hover:text-slate-200 transition"
                >
                  Depois
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
